import React, { useEffect, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { FiMove, FiX } from 'react-icons/fi';

const MIN_ZOOM = 1;

interface CoverCropModalProps {
  isOpen: boolean;
  imageSrc: string | null;
  isSubmitting?: boolean;
  aspect?: number;
  title?: string;
  description?: string;
  cropShape?: 'rect' | 'round';
  objectFit?: 'contain' | 'cover' | 'horizontal-cover' | 'vertical-cover';
  onCancel: () => void;
  onConfirm: (cropAreaPixels: Area) => void | Promise<void>;
}

export const CoverCropModal: React.FC<CoverCropModalProps> = ({
  isOpen,
  imageSrc,
  isSubmitting = false,
  aspect = 3 / 1,
  title = 'Adjust cover crop',
  description = 'Drag to choose what appears in your cover',
  cropShape = 'rect',
  objectFit: initialObjectFit = 'contain',
  onCancel,
  onConfirm,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [objectFit, setObjectFit] = useState<
    'contain' | 'cover' | 'horizontal-cover' | 'vertical-cover'
  >(initialObjectFit);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setCrop({ x: 0, y: 0 });
    setZoom(MIN_ZOOM);
    setObjectFit(initialObjectFit);
    setCroppedAreaPixels(null);
  }, [isOpen, imageSrc, initialObjectFit]);

  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) onCancel();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [isOpen, isSubmitting, onCancel]);

  if (!isOpen || !imageSrc) return null;

  return (
    <div className="fixed inset-0 z-[110] bg-black/75 backdrop-blur-sm p-4 md:p-6 flex items-center justify-center">
      <div className="w-full max-w-4xl rounded-2xl border border-white/10 bg-[#0f0f10] overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <h3 className="text-white text-lg font-semibold">{title}</h3>
            <p className="text-zinc-400 text-xs mt-1">{description}</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-50"
            aria-label="Close crop modal"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="p-5 md:p-6">
          <div className="relative w-full rounded-xl overflow-hidden border border-white/10 bg-zinc-950/80 h-[220px] md:h-[360px]">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              minZoom={MIN_ZOOM}
              maxZoom={5}
              aspect={aspect}
              cropShape={cropShape}
              restrictPosition={true}
              showGrid={false}
              objectFit={objectFit}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
            />
          </div>

          <div className="mt-5 flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2 text-zinc-400 text-sm">
              <div className="flex items-center gap-2">
                <FiMove size={14} />
                <span>Drag image to position</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setObjectFit((prev) => (prev === 'contain' ? 'cover' : 'contain'));
                  setZoom(MIN_ZOOM);
                  setCrop({ x: 0, y: 0 });
                }}
                className="text-xs font-medium text-blue-400 hover:text-blue-300 bg-white/5 hover:bg-white/10 px-3 py-1 rounded-full border border-white/10 transition-colors"
              >
                Fit: {objectFit === 'contain' ? 'Entire Image (Contain)' : 'Fill Box (Cover)'}
              </button>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-zinc-500 text-xs w-8">1x</span>
              <input
                type="range"
                min={MIN_ZOOM}
                max={5}
                step={0.01}
                value={zoom}
                onChange={(event) => setZoom(Number(event.target.value))}
                className="flex-1 accent-blue-500"
                aria-label="Zoom crop"
              />
              <span className="text-zinc-500 text-xs w-8 text-right">{zoom.toFixed(1)}x</span>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-white/10 flex items-center justify-end gap-2 bg-black/30">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 rounded-lg border border-white/12 text-zinc-300 hover:text-white hover:border-white/25 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!croppedAreaPixels || isSubmitting}
            onClick={() => {
              if (!croppedAreaPixels) return;
              void onConfirm(croppedAreaPixels);
            }}
            className="px-4 py-2 rounded-lg bg-white text-black font-semibold hover:bg-zinc-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Applying...' : 'Apply crop'}
          </button>
        </div>
      </div>
    </div>
  );
};
